/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { BehaviorSubject, Observable, iif, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, filter, map, retryWhen, switchMap, withLatestFrom } from 'rxjs/operators';

import { isElementNull } from '../../../../utils';
import { INotificationPreferences } from '../../components/notifications';
import {
  EFeatureId,
  EPermissionId,
  IFeaturesResponse,
  INotificationPreferencesResponse,
  IPermissionsResponse,
  IUserDataResponse,
  IUserFeatures,
  IUserPermissions,
  IUserSettings
} from '../../models';
import { EUserType } from '../../models/user-type.enum';
import { delayedRetry } from '../../operators';
import { UserDataHttpService } from '../../services';
import { USER_SETTINGS_TOKEN } from '../../tokens';
import {
  EUserMapSettingsActions,
  GetReplayUserSettings,
  GetReplayUserSettingsSuccess,
  GetUserMapSettings,
  GetUserMapSettingsSuccess,
  SetUserSettingsTokenValue
} from '../actions';
import { GetSelectedDeviceHealthStatus } from '../actions/device-health.actions';
import { getReplayUserSettings } from '../selectors';
import { IAppState, IUserMapSettingsState } from '../state';

@Injectable()
export class UserMapSettingsEffects {
  public getUserMapSettings$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetUserMapSettings>(EUserMapSettingsActions.GetUserMapSettings),
      switchMap(() =>
        this._userDataHttpService
          .getUserPermissions()
          .pipe(
            retryWhen(errors =>
              errors.pipe(concatMap((error, i) => iif(() => isPlatformBrowser(this._platformId), timer(i * 300), throwError(error))))
            )
          )
      ),
      map((response: IUserDataResponse) => this._mapUserDataResponseToStateModel(response)),
      switchMap((userMapSettings: IUserMapSettingsState) => {
        return [new GetUserMapSettingsSuccess(userMapSettings), new GetSelectedDeviceHealthStatus()];
      })
    )
  );

  public getReplayUserSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetReplayUserSettings>(EUserMapSettingsActions.GetReplayUserSettings),
      filter(() => isPlatformBrowser(this._platformId)),
      withLatestFrom(this._store.pipe(select(getReplayUserSettings))),
      filter(([, replayUserSettings]) => isElementNull(replayUserSettings)),
      switchMap(() =>
        this._userDataHttpService.getReplaySettings().pipe(
          delayedRetry(),
          catchError(() => of(null))
        )
      ),
      filter(response => !isElementNull(response)),
      map(replayUserSettings => new GetReplayUserSettingsSuccess(replayUserSettings))
    )
  );

  public pushUserSettingsValueToToken$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType<SetUserSettingsTokenValue>(EUserMapSettingsActions.SetUserSettingsTokenValue),
        map(action => action.payload),
        map(userSettings => {
          this._userSettingsToken.next(userSettings);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<IAppState>,
    private readonly _userDataHttpService: UserDataHttpService,
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    @Inject(USER_SETTINGS_TOKEN) private readonly _userSettingsToken: BehaviorSubject<IUserSettings>
  ) {}

  private _mapUserDataResponseToStateModel(response: IUserDataResponse): IUserMapSettingsState {
    response.UserSettings.TimeFormat = response.UserSettings.TimeFormat.replace('tt', 'a');
    return {
      userName: response.UserName,
      accountId: response.AccountId,
      accountSettings: response.AccountSettings,
      features: this._getUserFeatures(response.Features),
      notificationPreferences: this._getNotificationPreferences(response.NotificationPreferences),
      permissions: this._getUserPermissions(response.Permissions),
      universalAccountId: response.UniversalAccountId,
      userRowGuid: response.RowGuid,
      userSettings: response.UserSettings,
      webSocketAccountId: response.WebSocketAccountId,
      webSocketUserId: response.WebSocketUserId,
      featureToggles: response.FeatureToggles,
      userType: EUserType[response.UserType]
    };
  }

  private _getNotificationPreferences(preferences: INotificationPreferencesResponse[]): INotificationPreferences[] {
    const preferencesRefined: INotificationPreferences[] = [];

    if (!isElementNull(preferences)) {
      preferences.forEach(preference => {
        preferencesRefined.push({
          audioEnabled: preference.AudioEnabled,
          type: preference.Type,
          temporaryPopup: preference.TemporaryPopup,
          stationaryPopup: preference.StationaryPopup,
          numberBadge: preference.NumberBadge
        });
      });
    }

    return preferencesRefined;
  }

  private _getUserFeatures(features: IFeaturesResponse[]): IUserFeatures {
    const mapFeatures: Map<number, string> = new Map([
      [EFeatureId.CustomBalloons, 'customBalloons'],
      [EFeatureId.DrivingStyle, 'drivingStyle'],
      [EFeatureId.Privacy, 'privacy'],
      [EFeatureId.DriverApp, 'driverApp'],
      [EFeatureId.Towing, 'towing'],
      [EFeatureId.MisLive, 'misLive'],
      [EFeatureId.RealTimeTachoDate, 'realTimeTachoData'],
      [EFeatureId.Ecm, 'ecm'],
      [EFeatureId.DeviceStatusChange, 'deviceStatusChange'],
      [EFeatureId.AssetTracking, 'assets'],
      [EFeatureId.VideoExperience, 'videoExperience'],
      [EFeatureId.RoadSideAssistance, 'roadSideAssistance'],
      [EFeatureId.VideoAccess, 'videoAccess'],
      [EFeatureId.MandatoryGeofenceGroups, 'mandatoryGeofenceGroups'],
      [EFeatureId.ManagePostedSpeed, 'managePostedSpeed']
    ]);

    const resultFeatures = {} as IUserFeatures;

    mapFeatures.forEach((value: string, key: number) => {
      const userFeature = features.find(feature => feature.FeatureId === key);
      resultFeatures[value] = userFeature ? userFeature.EnabledStatus : false;
    });

    return resultFeatures;
  }

  private _getUserPermissions(permissions: IPermissionsResponse[]): IUserPermissions {
    const mapPermissions: Map<number, string> = new Map([
      [EPermissionId.ManageDrivers, 'manageDrivers'],
      [EPermissionId.ManageVehicles, 'manageVehicles'],
      [EPermissionId.ShowSpeedLimits, 'showSpeedLimits'],
      [EPermissionId.Places, 'places'],
      [EPermissionId.Dispatch, 'dispatch'],
      [EPermissionId.Geofences, 'geofences'],
      [EPermissionId.EnableIntegrations, 'enableIntegrations'],
      [EPermissionId.LiveMapFindNearest, 'liveMapFindNearest'],
      [EPermissionId.LiveMapSendStopsToInVehicleNavigationDevices, 'liveMapSendStopsToInVehicleNavigationDevices'],
      [EPermissionId.Replay, 'replay'],
      [EPermissionId.LiveMapWorkOrders, 'liveMapWorkOrders'],
      [EPermissionId.LiveMapWorkOrdersAssigned, 'liveMapWorkOrdersAssigned'],
      [EPermissionId.LiveMapWorkOrdersUnassigned, 'liveMapWorkOrdersUnassigned'],
      [EPermissionId.DispatchWindow, 'dispatchWindow'],
      [EPermissionId.DispatchWindowMessages, 'dispatchWindowMessages'],
      [EPermissionId.DispatchWindowStops, 'dispatchWindowStops'],
      [EPermissionId.Directions, 'directions'],
      [EPermissionId.DirectionsSendToVehicle, 'directionsSendToVehicle'],
      [EPermissionId.Alerts, 'alerts'],
      [EPermissionId.ReportDetailed, 'reportDetailed'],
      [EPermissionId.ReportJourneyAndStops, 'reportJourneyAndStops'],
      [EPermissionId.LiveMapTextMessaging, 'liveMapTextMessaging'],
      [EPermissionId.ShowVehicleSpeed, 'showVehicleSpeed'],
      [EPermissionId.LiveChat, 'liveChat'],
      [EPermissionId.ManagePlaces, 'managePlaces'],
      [EPermissionId.SuggestedGeofences, 'suggestedGeofences'],
      [EPermissionId.CreatePlaceFromMap, 'createPlaceFromMap'],
      [EPermissionId.ManageDriverDispatch, 'manageDriverDispatch'],
      [EPermissionId.RoutingLiveMap, 'routingLiveMap'],
      [EPermissionId.ShowTowingNotifications, 'showTowingNotifications'],
      [EPermissionId.ShowGPSSignalStrength, 'showGPSSignalStrength'],
      [EPermissionId.AssetTracking, 'assetTracking'],
      [EPermissionId.ManageAssets, 'manageAssets'],
      [EPermissionId.DriverPlannerPage, 'driverPlannerPage'],
      [EPermissionId.RoadSideAssistance, 'roadSideAssistance'],
      [EPermissionId.Scheduler, 'scheduler'],
      [EPermissionId.VideoExperience, 'videoExperience'],
      [EPermissionId.VideoAccess, 'videoAccess'],
      [EPermissionId.VideoGetVideo, 'videoGetVideo'],
      [EPermissionId.ECMFuelLevel, 'ecmFuelLevel'],
      [EPermissionId.DiagnosticTroubleCode, 'diagnosticTroubleCode'],
      [EPermissionId.FleetInspections, 'fleetInspections'],
      [EPermissionId.Insights, 'insights'],
      [EPermissionId.ManagePostedSpeed, 'managePostedSpeed']
    ]);

    const resultPermissions = permissions.reduce((result, element) => {
      result[mapPermissions.get(element.PermissionId)] = element.Status;
      return result;
    }, {} as IUserPermissions);

    return resultPermissions;
  }
}
